<template>
  <div class="teacher_data">
    <div class="personal_data_wrapper fadeIn">
      <!-- Start Hero Profile Header Card -->
      <div class="profile-hero-card mb-5">
        <div class="hero-main-info">
          <div class="user-avatar-wrapper">
            <img
              v-if="userInfo.avatar"
              :src="userInfo.avatar"
              alt="Avatar"
              class="user-avatar"
            />
            <div v-else class="avatar-placeholder">
              <i class="fas fa-user"></i>
            </div>
            <span
              class="status-indicator-dot"
              :class="userInfo.is_online ? 'online' : 'offline'"
            ></span>
          </div>

          <div class="user-title-details">
            <div class="d-flex align-items-center gap-2 flex-wrap mb-1">
              <h2 class="user-name">{{ userInfo.full_name || "---" }}</h2>
              <span class="user-id-badge">ID: #{{ userInfo.id }}</span>
            </div>
            <p class="user-subtitle dir-ltr" v-if="userInfo.phone">
              <i class="fas fa-phone-alt ml-1"></i>
              {{ userInfo.phone_code }} {{ userInfo.phone }}
            </p>
          </div>
        </div>

        <div class="hero-actions">
          <router-link
            :to="`/client/edit/${userInfo.id}`"
            class="action-btn btn-edit"
          >
            <i class="fas fa-user-edit"></i>
            <span>تعديل بيانات العميل</span>
          </router-link>

          <button
            class="action-btn btn-balance"
            :disabled="balanceLoading"
            @click="isChangeBalanceModel = true"
          >
            <i class="fas fa-exchange-alt"></i>
            <span>تحويل رصيد</span>
          </button>

          <button
            class="action-btn"
            :class="userInfo.is_ban ? 'btn-unban' : 'btn-ban'"
            @click="toggleStatusModel"
          >
            <i
              :class="
                userInfo.is_ban ? 'fas fa-user-check' : 'fas fa-user-slash'
              "
            ></i>
            <span>{{ userInfo.is_ban ? "إلغاء الحظر" : "حظر المستخدم" }}</span>
          </button>
        </div>
      </div>
      <!-- End Hero Profile Header Card -->

      <!-- Start Stats Grid -->
      <div class="order-details-page mb-5 p-2">
        <div class="magic-stats-grid">
          <!-- Name -->
          <div class="magic-stat-card">
            <div class="stat-icon-wrapper blue-gradient">
              <i class="fas fa-user"></i>
            </div>
            <div class="stat-content">
              <span class="stat-label">{{ $t("forms.labels.name") }}</span>
              <h4 class="stat-value">{{ userInfo.full_name || "---" }}</h4>
            </div>
          </div>

          <!-- Phone -->
          <div class="magic-stat-card" v-if="userInfo.phone">
            <div class="stat-icon-wrapper green-gradient">
              <i class="fas fa-phone-alt"></i>
            </div>
            <div class="stat-content">
              <span class="stat-label">{{ $t("forms.labels.phone") }}</span>
              <h4 class="stat-value" dir="ltr">
                {{ userInfo.phone_code }} {{ userInfo.phone }}
              </h4>
            </div>
          </div>

          <!-- Email -->
          <div class="magic-stat-card">
            <div class="stat-icon-wrapper orange-gradient">
              <i class="fas fa-envelope"></i>
            </div>
            <div class="stat-content">
              <span class="stat-label">{{ $t("forms.labels.email") }}</span>
              <h4 class="stat-value">{{ userInfo.email || "لا يوجد" }}</h4>
            </div>
          </div>

          <!-- Country -->
          <div class="magic-stat-card" v-if="userInfo.country">
            <div class="stat-icon-wrapper cyan-gradient">
              <i class="fas fa-globe-africa"></i>
            </div>
            <div class="stat-content">
              <span class="stat-label">{{ $t("forms.labels.country") }}</span>
              <h4 class="stat-value">{{ userInfo.country.name }}</h4>
            </div>
          </div>

          <!-- City -->
          <div class="magic-stat-card" v-if="userInfo.city">
            <div class="stat-icon-wrapper pink-gradient">
              <i class="fas fa-city"></i>
            </div>
            <div class="stat-content">
              <span class="stat-label">{{ $t("forms.labels.city") }}</span>
              <h4 class="stat-value">{{ userInfo.city.name }}</h4>
            </div>
          </div>

          <!-- Wallet -->
          <div class="magic-stat-card">
            <div class="stat-icon-wrapper indigo-gradient">
              <i class="fas fa-wallet"></i>
            </div>
            <div class="stat-content">
              <span class="stat-label">المحفظة</span>
              <h4 class="stat-value">{{ userInfo.wallet || 0 }}</h4>
            </div>
          </div>

          <!-- Active Status -->
          <div class="magic-stat-card" v-if="userInfo.is_active !== undefined">
            <div
              class="stat-icon-wrapper"
              :class="userInfo.is_active ? 'green-gradient' : 'red-gradient'"
            >
              <i
                :class="
                  userInfo.is_active ? 'fas fa-user-check' : 'fas fa-user-times'
                "
              ></i>
            </div>
            <div class="stat-content">
              <span class="stat-label">حالة التفعيل</span>
              <h4
                class="stat-value"
                :class="userInfo.is_active ? 'text-success' : 'text-danger'"
              >
                {{ userInfo.is_active ? "مفعل" : "غير مفعل" }}
              </h4>
            </div>
          </div>

          <!-- Online Status -->
          <div class="magic-stat-card" v-if="userInfo.is_online !== undefined">
            <div
              class="stat-icon-wrapper"
              :class="userInfo.is_online ? 'teal-gradient' : 'yellow-gradient'"
            >
              <i
                :class="
                  userInfo.is_online ? 'fas fa-wifi' : 'fas fa-wifi-slash'
                "
              ></i>
            </div>
            <div class="stat-content">
              <span class="stat-label">حالة الاتصال</span>
              <h4
                class="stat-value"
                :class="userInfo.is_online ? 'text-success' : 'text-danger'"
              >
                {{ userInfo.is_online ? "متصل" : "غير متصل" }}
              </h4>
            </div>
          </div>

          <!-- Delivery Orders Count (Clickable Card) -->
          <router-link
            class="magic-stat-card interactive-card"
            :to="{
              path: '/delivery_orders/show-all',
              query: { ...$route.query, client_id: userInfo.id },
            }"
          >
            <div class="stat-icon-wrapper blue-gradient">
              <i class="fas fa-truck-loading"></i>
            </div>
            <div class="stat-content flex-grow-1">
              <span class="stat-label">عدد طلبات التوصيل</span>
              <h4 class="stat-value">
                {{ userInfo.orders_delivery_count || 0 }}
              </h4>
            </div>
            <div class="arrow-icon">
              <i class="fas fa-arrow-left"></i>
            </div>
          </router-link>

          <!-- Package Orders Count (Clickable Card) -->
          <router-link
            class="magic-stat-card interactive-card"
            :to="{
              path: '/package_order/show-all',
              query: { ...$route.query, client_id: userInfo.id },
            }"
          >
            <div class="stat-icon-wrapper purple-gradient">
              <i class="fas fa-box-open"></i>
            </div>
            <div class="stat-content flex-grow-1">
              <span class="stat-label">عدد طلبات الطرود</span>
              <h4 class="stat-value">
                {{ userInfo.orders_package_count || 0 }}
              </h4>
            </div>
            <div class="arrow-icon">
              <i class="fas fa-arrow-left"></i>
            </div>
          </router-link>

          <!-- Total Finished Orders Value -->
          <div class="magic-stat-card">
            <div class="stat-icon-wrapper orange-gradient">
              <i class="fas fa-coins"></i>
            </div>
            <div class="stat-content">
              <span class="stat-label">قيمة الطلبات المنتهية</span>
              <h4 class="stat-value">
                {{ userInfo.total_finished_orders_price || 0 }}
              </h4>
            </div>
          </div>

          <!-- Ban Status -->
          <div class="magic-stat-card">
            <div
              class="stat-icon-wrapper"
              :class="userInfo.is_ban ? 'red-gradient' : 'green-gradient'"
            >
              <i
                :class="userInfo.is_ban ? 'fas fa-ban' : 'fas fa-shield-alt'"
              ></i>
            </div>
            <div class="stat-content">
              <span class="stat-label">حالة الحظر</span>
              <h4
                class="stat-value"
                :class="userInfo.is_ban ? 'text-danger' : 'text-success'"
              >
                {{ userInfo.is_ban ? "محظور" : "غير محظور" }}
              </h4>
            </div>
          </div>

          <!-- Ban Reason -->
          <div
            class="magic-stat-card full-width-card"
            v-if="userInfo.is_ban && userInfo.ban_reason"
          >
            <div class="stat-icon-wrapper red-gradient">
              <i class="fas fa-exclamation-triangle"></i>
            </div>
            <div class="stat-content">
              <span class="stat-label">سبب الحظر</span>
              <h4 class="stat-value text-danger">{{ userInfo.ban_reason }}</h4>
            </div>
          </div>
        </div>
      </div>
      <!-- End Stats Grid -->
    </div>

    <!-- Modals -->
    <v-dialog v-model="isDeactivationModel" width="500">
      <v-card class="p-4 rounded-xl">
        <form @submit.prevent>
          <h4 class="main_color font-weight-bold text-center mb-3">
            إلغاء التفعيل
          </h4>
          <div class="input_wrapper top_label mb-4">
            <label for="desc" class="form-label">سبب إلغاء التفعيل</label>
            <textarea
              class="form-control"
              id="desc"
              v-model.trim="deactivationReason"
              rows="4"
            ></textarea>
          </div>
          <div class="text-center">
            <v-btn
              depressed
              color="error"
              class="rounded-lg px-6"
              :loading="activeLoading"
              @click="toggleActive(0)"
            >
              إلغاء التفعيل
            </v-btn>
          </div>
        </form>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isChangeBalanceModel" width="420">
      <v-card class="p-4 rounded-xl">
        <form @submit.prevent="toggleBalance">
          <h4 class="main_color font-weight-bold text-center mb-4">
            <i class="fas fa-exchange-alt ml-2"></i>تحويل رصيد
          </h4>

          <div class="input_wrapper top_label mb-3">
            <label class="form-label">نوع التحويل</label>
            <multiselect
              v-model="transaction.type"
              :options="types"
              label="name"
              track-by="name"
              placeholder="اختر النوع"
              :searchable="true"
              :allow-empty="false"
              :show-labels="false"
            ></multiselect>
          </div>

          <div class="input_wrapper top_label mb-3">
            <label class="form-label">المبلغ</label>
            <input
              type="number"
              class="form-control"
              v-model="transaction.amount"
              placeholder="أدخل المبلغ"
            />
          </div>

          <div class="input_wrapper top_label mb-4">
            <label class="form-label">ملاحظة (اختياري)</label>

            <textarea
              class="form-control"
              v-model="transaction.note"
              rows="3"
              placeholder="اكتب ملاحظة..."
            ></textarea>
          </div>

          <div class="text-center">
            <v-btn
              type="submit"
              depressed
              color="primary"
              class="rounded-lg px-8 py-2"
              :loading="balanceLoading"
            >
              حفظ
            </v-btn>
          </div>
        </form>
      </v-card>
    </v-dialog>

    <v-dialog v-model="statusModel" width="460">
      <v-card class="p-4 rounded-xl">
        <form @submit.prevent="changeStatus">
          <h4 class="main_color font-weight-bold text-center mb-3">
            <i
              :class="
                userInfo.is_ban ? 'fas fa-user-check' : 'fas fa-user-slash'
              "
              class="ml-2"
            ></i>
            {{ userInfo.is_ban ? "إلغاء حظر المستخدم" : "حظر المستخدم" }}
          </h4>

          <p class="text-center text-muted mb-4">
            {{
              userInfo.is_ban
                ? "هل أنت تأكد من إمكانية إلغاء الحظر لهذا المستخدم؟"
                : "يرجى تحديد سبب حظر هذا المستخدم"
            }}
          </p>

          <div class="input_wrapper top_label mb-4" v-if="!userInfo.is_ban">
            <label class="form-label">سبب الحظر</label>
            <textarea
              class="form-control"
              v-model="ban_reason"
              rows="4"
              placeholder="اكتب سبب الحظر..."
            ></textarea>
          </div>

          <div class="d-flex justify-content-center gap-3">
            <v-btn
              type="submit"
              depressed
              :color="userInfo.is_ban ? 'success' : 'error'"
              class="rounded-lg px-6"
              :loading="statusLoading"
            >
              {{ userInfo.is_ban ? "إلغاء الحظر" : "حظر" }}
            </v-btn>

            <v-btn
              depressed
              color="secondary"
              class="rounded-lg px-6"
              @click="statusModel = false"
            >
              إلغاء
            </v-btn>
          </div>
        </form>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  props: ["userInfo"],

  data() {
    return {
      deactivationReason: null,
      activeLoading: false,
      isDeactivationModel: false,
      isChangeBalanceModel: false,
      balanceLoading: false,

      transaction: {
        type: null,
        amount: null,
        note: null,
        reason: null,
      },

      types: [
        { id: "withdraw", name: "سحب" },
        { id: "charge", name: "إيداع" },
      ],

      loading: false,
      statusModel: false,
      ban_reason: null,
      statusLoading: false,
    };
  },

  methods: {
    toggleBalance() {
      if (!this.transaction.type) {
        this.$iziToast.error({
          timeout: 2000,
          message: "اختر نوع التحويل",
          position: "bottomCenter",
        });
        return;
      }
      if (!this.transaction.amount) {
        this.$iziToast.error({
          timeout: 2000,
          message: "أدخل المبلغ",
          position: "bottomCenter",
        });
        return;
      }

      this.balanceLoading = true;
      const formData = new FormData();
      formData.append("amount", this.transaction.amount);
      if (this.transaction.note) {
        formData.append("note", this.transaction.note);
      }

      this.$axios
        .post(`${this.transaction.type.id}/${this.userInfo.id}`, formData)
        .then((res) => {
          this.balanceLoading = false;
          this.isChangeBalanceModel = false;
          this.transaction.amount = null;
          this.transaction.type = null;
          this.transaction.note = null;
          this.$iziToast.success({
            timeout: 2000,
            message: res.data.message,
            position: "bottomCenter",
          });
          this.$emit("get_item");
        })
        .catch((err) => {
          this.balanceLoading = false;
          this.$iziToast.error({
            timeout: 2000,
            message: err.response?.data?.message || "حدث خطأ ما",
            position: "bottomCenter",
          });
        });
    },

    toggleStatusModel() {
      this.statusModel = true;
    },

    changeStatus() {
      if (!this.userInfo.is_ban && !this.ban_reason) {
        this.$iziToast.error({
          message: "يرجى إدخال سبب الحظر أولاً",
          timeout: 4000,
          position: "bottomRight",
        });
        return;
      }
      this.statusLoading = true;
      const formData = new FormData();
      formData.append("is_active", this.userInfo.is_ban ? 0 : 1);
      if (this.ban_reason) {
        formData.append("message", this.ban_reason);
      }
      formData.append("_method", "PUT");

      this.$axios({
        method: "POST",
        url: `/client/is_active/${this.userInfo.id}`,
        data: formData,
      })
        .then((res) => {
          this.$iziToast.success({
            timeout: 4000,
            message: res.data.message,
            position: "bottomRight",
          });
          this.statusModel = false;
          this.$emit("get_item");
          this.ban_reason = null;
          this.statusLoading = false;
        })
        .catch(() => {
          this.statusLoading = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
/* Profile Hero Banner Card */
.profile-hero-card {
  background: var(--cardBgColor, #ffffff);
  border-radius: 24px;
  padding: 24px 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04);
  border: 1px solid var(--borderColor, #e2e8f0);
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 20px;

  .hero-main-info {
    display: flex;
    align-items: center;
    gap: 20px;

    .user-avatar-wrapper {
      position: relative;
      width: 72px;
      height: 72px;

      .user-avatar {
        width: 100%;
        height: 100%;
        border-radius: 20px;
        object-fit: cover;
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
        border: 3px solid #ffffff;
      }

      .avatar-placeholder {
        width: 100%;
        height: 100%;
        border-radius: 20px;
        background: linear-gradient(
          135deg,
          rgba(120, 61, 115, 0.15) 0%,
          rgba(200, 70, 227, 0.08) 100%
        );
        color: var(--mainColor-2, #783d73);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.8rem;
        border: 2px solid rgba(120, 61, 115, 0.2);
      }

      .status-indicator-dot {
        position: absolute;
        bottom: -2px;
        inset-inline-end: -2px;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        border: 3px solid #ffffff;

        &.online {
          background-color: #10b981;
          box-shadow: 0 0 8px rgba(16, 185, 129, 0.6);
        }

        &.offline {
          background-color: #94a3b8;
        }
      }
    }

    .user-title-details {
      .user-name {
        font-size: 1.4rem;
        font-weight: 800;
        color: var(--mainColor-2, #783d73);
        margin: 0;
      }

      .user-id-badge {
        background: rgba(120, 61, 115, 0.1);
        color: var(--mainColor-2, #783d73);
        font-size: 12px;
        font-weight: 700;
        padding: 3px 10px;
        border-radius: 20px;
        border: 1px solid rgba(120, 61, 115, 0.15);
      }

      .user-subtitle {
        font-size: 13.5px;
        color: #64748b;
        margin: 0;
        font-weight: 600;
      }
    }
  }

  .hero-actions {
    display: flex;
    align-items: center;
    gap: 12px;

    .action-btn {
      padding: 10px 20px;
      border-radius: 14px;
      font-size: 14px;
      font-weight: 700;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      border: none;
      cursor: pointer;
      transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
      box-shadow: 0 4px 14px rgba(0, 0, 0, 0.05);
      text-decoration: none !important;

      &:hover {
        transform: translateY(-2px);
      }

      &.btn-edit {
        background: linear-gradient(
          135deg,
          var(--mainColor-2, #783d73) 0%,
          #9b4f95 100%
        );
        color: #ffffff !important;

        &:hover {
          box-shadow: 0 6px 20px rgba(120, 61, 115, 0.35);
          color: #ffffff !important;
        }
      }

      &.btn-balance {
        background: linear-gradient(135deg, #10b981 0%, #059669 100%);
        color: #ffffff;

        &:hover {
          box-shadow: 0 6px 20px rgba(16, 185, 129, 0.35);
        }
      }

      &.btn-ban {
        background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
        color: #ffffff;

        &:hover {
          box-shadow: 0 6px 20px rgba(239, 68, 68, 0.35);
        }
      }

      &.btn-unban {
        background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
        color: #ffffff;

        &:hover {
          box-shadow: 0 6px 20px rgba(59, 130, 246, 0.35);
        }
      }
    }
  }
}

/* Magic Stats Grid */
.magic-stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 20px;
}

.magic-stat-card {
  background: var(--cardBgColor, #ffffff);
  border-radius: 20px;
  padding: 20px 22px;
  display: flex;
  align-items: center;
  gap: 18px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
  border: 1px solid var(--borderColor, #e2e8f0);
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  text-decoration: none;

  &.full-width-card {
    grid-column: 1 / -1;
  }

  &.interactive-card {
    cursor: pointer;

    .arrow-icon {
      color: #94a3b8;
      font-size: 14px;
      transition: transform 0.25s ease, color 0.25s ease;
    }

    &:hover .arrow-icon {
      transform: translateX(-5px);
      color: var(--mainColor-2, #783d73);
    }
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 14px 35px rgba(120, 61, 115, 0.12);
    border-color: rgba(120, 61, 115, 0.25);

    .stat-icon-wrapper {
      transform: scale(1.1) rotate(4deg);
    }
  }

  .stat-icon-wrapper {
    width: 52px;
    height: 52px;
    border-radius: 16px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 1.4rem;
    color: #ffffff;
    transition: transform 0.35s ease;
    flex-shrink: 0;

    &.blue-gradient {
      background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
      box-shadow: 0 8px 20px rgba(59, 130, 246, 0.28);
    }
    &.purple-gradient {
      background: linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%);
      box-shadow: 0 8px 20px rgba(139, 92, 246, 0.28);
    }
    &.orange-gradient {
      background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
      box-shadow: 0 8px 20px rgba(245, 158, 11, 0.28);
    }
    &.green-gradient {
      background: linear-gradient(135deg, #10b981 0%, #047857 100%);
      box-shadow: 0 8px 20px rgba(16, 185, 129, 0.28);
    }
    &.cyan-gradient {
      background: linear-gradient(135deg, #06b6d4 0%, #0e7490 100%);
      box-shadow: 0 8px 20px rgba(6, 182, 212, 0.28);
    }
    &.pink-gradient {
      background: linear-gradient(135deg, #ec4899 0%, #be185d 100%);
      box-shadow: 0 8px 20px rgba(236, 72, 153, 0.28);
    }
    &.red-gradient {
      background: linear-gradient(135deg, #ef4444 0%, #b91c1c 100%);
      box-shadow: 0 8px 20px rgba(239, 68, 68, 0.28);
    }
    &.indigo-gradient {
      background: linear-gradient(135deg, #6366f1 0%, #4338ca 100%);
      box-shadow: 0 8px 20px rgba(99, 102, 241, 0.28);
    }
    &.teal-gradient {
      background: linear-gradient(135deg, #14b8a6 0%, #0f766e 100%);
      box-shadow: 0 8px 20px rgba(20, 184, 166, 0.28);
    }
    &.yellow-gradient {
      background: linear-gradient(135deg, #eab308 0%, #ca8a04 100%);
      box-shadow: 0 8px 20px rgba(234, 179, 8, 0.28);
    }
  }

  .stat-content {
    display: flex;
    flex-direction: column;

    .stat-label {
      font-size: 0.85rem;
      color: #64748b;
      margin-bottom: 4px;
      font-weight: 600;
    }

    .stat-value {
      font-size: 1.18rem;
      font-weight: 800;
      color: var(--mainText, #2c2c2c);
      margin: 0;
    }
  }
}

// Dark Mode Overrides
html.darkTheme {
  .profile-hero-card {
    background: #1e1e2d;
    border-color: #2b2b40;
  }

  .magic-stat-card {
    background: #1e1e2d;
    border-color: #2b2b40;

    .stat-content .stat-value {
      color: #f1f5f9;
    }
  }
}
</style>
